import { deserify, QuestionContainerWithSolution } from '@geneo2-web/shared-ui';
import {
  Question,
  Question_QuestionEnum,
} from '@protos/content_management/content.db_pb';
import {
  QAttemptResultEnum,
  SessionStatusEnum,
} from '@protos/learning_management/lms.db_pb';
import { Timestamp } from '@bufbuild/protobuf';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLiveQuizContext } from '../../app/Context/LiveQuizContextProvider';
import { useAppDispatch, useAppSelector } from '../../reduxStore/reduxHooks';
import {
  HOME,
  LIVE_QUIZ_WAITING_ROOM,
} from '../../routeHandling/RoutesNomenclature';
import { setHasAnsweredCurrent } from './reducer/liveQuiz.slice';
import { useGlobalContext } from '../../app/Context/GlobalContextProvider';

// Renders strictly off redux state populated by the context provider's
// Postgres-backed resync (never off locally-held "next question" state) —
// there is deliberately no way for this screen to advance itself; only the
// teacher ending the question (via endLiveQuizQuestion, observed through the
// Firestore listener) moves things forward. Question content was fetched
// once at join time (and again on every resync) into `questions` — this
// screen only ever picks an already-cached item out of that array by
// questionId, never fetches per question.
export const LiveQuizAnswer = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { submitLiveQuizResponse } = useLiveQuizContext();
  const { userInfo } = deserify(useAppSelector((state) => state.auth));
  const {
    live_quiz_session,
    current_question,
    questions,
    has_answered_current,
    revealed_result,
    last_question_stats,
    session_status,
  } = deserify(useAppSelector((state) => state.liveQuiz));
  const { selectedFunction, setSelectedFunction } = useGlobalContext();
  const [selectedAnswer, setSelectedAnswer] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  // When this question actually became answerable — used as the response's
  // startTime so timeTakenInSec is measured correctly, not defaulted to zero.
  const questionStartTimeRef = useRef(new Date());
  // endLiveQuizQuestion clears current_question server-side the same moment
  // revealed_result becomes available, so this screen can't key its render
  // off current_question alone — it would blank out the question being
  // revealed right as the reveal appears. Remembers the last live question
  // so the reveal (or the "waiting for reveal" state) still has something to
  // show once current_question itself is gone.
  const [displayedQuestion, setDisplayedQuestion] = useState(current_question);
  useEffect(() => {
    if (current_question) setDisplayedQuestion(current_question);
  }, [current_question?.questionId]);

  const questionContent = questions.find(
    (q) => q.questionId === displayedQuestion?.questionId
  );
  const backClick = () => {
    navigate(HOME);
  };
  useEffect(() => {
    setSelectedFunction(() => backClick);

    return () => {
      setSelectedFunction(null);
    };
  }, []);
  useEffect(() => {
    if (!live_quiz_session) return; // reset already happened, we're leaving
    if (
      session_status === SessionStatusEnum.SESSION_STATUS_PAUSED ||
      session_status === SessionStatusEnum.SESSION_STATUS_COMPLETED ||
      session_status === SessionStatusEnum.SESSION_STATUS_EXITED
    ) {
      return;
    }
    if (!current_question && !revealed_result && !has_answered_current) {
      navigate(LIVE_QUIZ_WAITING_ROOM);
    }
  }, [current_question, revealed_result, has_answered_current, navigate]);

  useEffect(() => {
    setSelectedAnswer([]);
    questionStartTimeRef.current = new Date();
  }, [current_question?.liveQuizQuestionSessionId]);

  if (!live_quiz_session) return null;
  if (!displayedQuestion) return null;

  const handleAnswerChange = (val: string) => {
    if (
      questionContent?.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQM
    ) {
      setSelectedAnswer((prev) =>
        prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
      );
    } else {
      setSelectedAnswer([val]);
    }
  };

  const handleSubmit = async () => {
    const studentId = userInfo?.studentProfileId;
    if (!studentId || !selectedAnswer.length) return;
    setSubmitting(true);
    const ok = await submitLiveQuizResponse({
      studentId,
      liveQuizSessionId: live_quiz_session.liveQuizSessionId,
      liveQuizQuestionSessionId: displayedQuestion.liveQuizQuestionSessionId,
      questionId: displayedQuestion.questionId,
      response: {
        answer: selectedAnswer,
        startTime: Timestamp.fromDate(questionStartTimeRef.current),
        endTime: Timestamp.fromDate(new Date()),
      },
    });
    setSubmitting(false);
    if (ok) dispatch(setHasAnsweredCurrent(true));
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h3" color="primary.main">
        Question {displayedQuestion.sequence}
      </Typography>

      {revealed_result ? (
        <Box sx={{ mt: 2 }}>
          {questionContent && (
            <QuestionContainerWithSolution
              questionTypeDisplay
              question={new Question(questionContent)}
              userAttemptedAnswer={selectedAnswer}
              disableInput
              showAnswer
              defaultExpandedAccordion
              imageResizeEnabled
            />
          )}
          {/* <Typography
            variant="h6"
            sx={{ mt: 2 }}
            color={
              revealed_result.isCorrect === QAttemptResultEnum.RESPONSE_CORRECT
                ? 'success.main'
                : 'error.main'
            }
          >
            {revealed_result.isCorrect === QAttemptResultEnum.RESPONSE_CORRECT
              ? 'Correct!'
              : 'Incorrect'}{' '}
            — {revealed_result.responseScore} / {revealed_result.maxScore}
          </Typography>
          {last_question_stats && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Class: {last_question_stats.correctCount} of{' '}
              {last_question_stats.answeredCount} answered correctly
              {last_question_stats.unansweredCount > 0
                ? ` · ${last_question_stats.unansweredCount} didn't answer in time`
                : ''}
            </Typography>
          )} */}
        </Box>
      ) : has_answered_current ? (
        <Box sx={{ mt: 2 }}>
          {questionContent && (
            <QuestionContainerWithSolution
              questionTypeDisplay
              question={new Question(questionContent)}
              userAttemptedAnswer={selectedAnswer}
              disableInput
              imageResizeEnabled
            />
          )}
          <Typography variant="body1" sx={{ mt: 2 }}>
            Answer submitted — waiting for your teacher to reveal the result…
          </Typography>
        </Box>
      ) : questionContent ? (
        <>
          <QuestionContainerWithSolution
            questionTypeDisplay
            question={new Question(questionContent)}
            userAttemptedAnswer={selectedAnswer}
            handleAnswerChange={handleAnswerChange}
            disableInput={submitting}
            imageResizeEnabled
          />
          <Button
            fullWidth
            variant="contained"
            disabled={submitting || !selectedAnswer.length}
            onClick={handleSubmit}
            sx={{ mt: 2 }}
          >
            {submitting ? 'Submitting…' : 'Submit Answer'}
          </Button>
        </>
      ) : (
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading question…
        </Typography>
      )}
    </Box>
  );
};

export default LiveQuizAnswer;
